import { json } from "@remix-run/node";
import { useFetcher, useLocation } from "@remix-run/react";
import { useEffect, useRef } from "react";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";

/**
 * Linked from nav as GET /u/log-out. Clears client workflow token + org/team cache, then POSTs to
 * the same server `logout` as the logout forms (`app/routes/_auth+/logout.tsx`).
 */
export async function loader() {
  return json({});
}

export default function LogOutRoute() {
  const fetcher = useFetcher();
  const { key } = useLocation();
  const submittedFor = useRef<string | null>(null);

  useEffect(() => {
    // Submit ONCE per visit.
    //
    // `useFetcher()` hands back a new object identity on every state
    // transition (idle -> submitting -> loading -> idle), so with `fetcher` in
    // the dependency array the effect re-ran on its own result and submitted
    // again -- measured at seven `POST /logout` requests in six seconds. The
    // sign-out still completed, so it looked harmless, but each extra request
    // races the session it is in the middle of destroying: when one lost, the
    // route rendered "Unexpected Error / Failed to fetch" and the redirect
    // never happened.
    //
    // Keyed on the location key rather than a bare boolean so that navigating
    // to /u/log-out again still signs out again, which is why `key` was a
    // dependency in the first place.
    if (submittedFor.current === key) return;
    submittedFor.current = key;
    clearClientAuthStateOnLogout();
    fetcher.submit(new FormData(), { method: "post", action: "/logout" });
  }, [key, fetcher]);

  return (
    <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
      Signing out…
    </div>
  );
}
